import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { ArrowLeft, Lock, Loader2, ShoppingCart, Volume2, VolumeX, AlertTriangle } from 'lucide-react';
import { LanguageSelector } from '../components/LanguageSelector';
import { MyCompanionLogo } from '../components/MyCompanionLogo';
import { toast } from 'sonner';
import { usePurchase } from '../hooks/usePurchase';
import { usePlayPrices } from '../hooks/usePlayPrices';
import { runPurchaseWithRetry } from '../utils/purchaseRetry';
import { track } from '../utils/analytics';
import PurchaseCelebrationModal from '../components/PurchaseCelebrationModal';
import SmartImage from '../components/SmartImage';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SelectCharacterPage() {
  const navigate = useNavigate();
  const { gender } = useParams();
  const { user, token, logout, isCharacterUnlocked, refreshUser, isGuest } = useAuth();
  const { t, language } = useLanguage();
  const { startPurchase: _basePurchase } = usePurchase();
  const startPurchase = React.useCallback(
    (params) => runPurchaseWithRetry({ startPurchase: _basePurchase, params, toast, language }),
    [_basePurchase, language]
  );
  const playPrices = usePlayPrices();
  const [characters, setCharacters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);
  const [playingVoice, setPlayingVoice] = useState(null);
  const [loadingVoice, setLoadingVoice] = useState(null);
  const audioRef = useRef(null);
  const [guestWarningOpen, setGuestWarningOpen] = useState(false);
  const [pendingPurchaseChar, setPendingPurchaseChar] = useState(null);
  const [celebration, setCelebration] = useState(null);

  // Age verification removed — MyCompanion AI is a general-audience romantic companion app.

  useEffect(() => {
    fetchCharacters();
  }, [gender, language, token]);

  const fetchCharacters = async () => {
    try {
      const response = await axios.get(`${API}/characters/${gender}?language=${language}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCharacters(response.data);
    } catch (error) {
      console.error('Error fetching characters:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCardClick = (character) => {
    if (character.coming_soon) return;
    track('character_viewed', { character_name: character.name, character_id: character.id });
    const isUnlocked = character.is_unlocked || character.is_free;
    if (isUnlocked) {
      handleSelect(character);
    } else {
      handlePurchase(character);
    }
  };

  const handleSelect = (character) => {
    if (!character.is_unlocked && !character.is_free) {
      // Show purchase option
      return;
    }
    // Skip the level-selection step and go straight to the chat at level 1
    // (Discovery — always free). Users can still change level later from the
    // chat header (currency button → /select-level/...).
    navigate(`/chat/${gender}/${character.id}/1`);
  };

  const handlePurchase = async (character) => {
    // If guest, show warning modal first
    if (isGuest) {
      setPendingPurchaseChar(character);
      setGuestWarningOpen(true);
      return;
    }
    executePurchase(character);
  };

  const executePurchase = async (character) => {
    setPurchasing(character.id);
    try {
      const returnPath = `/chat/${gender}/${character.id}/1`;
      const res = await startPurchase({
        itemType: 'character',
        itemId: character.id,
        returnTo: returnPath,
      });
      if (res.redirected) return; // Stripe redirect happens
      if (res.success) {
        setCelebration({
          characterName: character.name,
          continueTo: returnPath,
        });
      } else if (!res.cancelled) {
        toast.error(res.message || (language === 'fr' ? "Erreur lors de l'achat" : 'Purchase error'));
      }
    } catch (error) {
      console.error('Purchase error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'achat');
    } finally {
      setPurchasing(null);
    }
  };

  const confirmGuestPurchase = () => {
    setGuestWarningOpen(false);
    if (pendingPurchaseChar) {
      executePurchase(pendingPurchaseChar);
      setPendingPurchaseChar(null);
    }
  };

  const genderLabels = {
    woman: { fr: 'Femmes', en: 'Women', es: 'Mujeres', de: 'Frauen', pt: 'Mulheres' },
    man: { fr: 'Hommes', en: 'Men', es: 'Hombres', de: 'Männer', pt: 'Homens' },
    couple: { fr: 'Couples', en: 'Couples', es: 'Parejas', de: 'Paare', pt: 'Casais' }
  };

  // Play character voice preview
  const playVoicePreview = async (character) => {
    // Stop current audio if playing
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    
    // If clicking the same character that's playing, just stop
    if (playingVoice === character.id) {
      setPlayingVoice(null);
      return;
    }
    
    setLoadingVoice(character.id);
    
    try {
      // Generate a greeting phrase for the character
      const greetings = {
        fr: `Bonjour, je suis ${character.name}. Ravie de faire ta connaissance...`,
        en: `Hello, I'm ${character.name}. Nice to meet you...`
      };
      
      const response = await axios.post(`${API}/tts/preview`, {
        text: greetings[language],
        character_id: character.id,
        language: language
      });
      
      if (response.data.audio_url) {
        const audio = new Audio(response.data.audio_url);
        audioRef.current = audio;
        
        audio.onended = () => {
          setPlayingVoice(null);
          audioRef.current = null;
        };
        
        audio.onerror = () => {
          setPlayingVoice(null);
          audioRef.current = null;
          toast.error(language === 'fr' ? 'Erreur de lecture audio' : 'Audio playback error');
        };
        
        await audio.play();
        setPlayingVoice(character.id);
      }
    } catch (error) {
      console.error('Voice preview error:', error);
      toast.error(language === 'fr' ? 'Voix indisponible' : 'Voice unavailable');
    } finally {
      setLoadingVoice(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-velvet-bg flex items-center justify-center">
        <Loader2 className="animate-spin text-purple-main" size={40} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-velvet-bg">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button 
            onClick={() => navigate('/select-gender')}
            className="flex items-center gap-2 text-velvet_text-secondary hover:text-velvet_text-accent transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft size={20} />
            <MyCompanionLogo size="sm" />
          </button>
          <div className="flex items-center gap-4">
            <LanguageSelector />
            <div className="flex items-center gap-3">
              <Button 
                variant="ghost" 
                onClick={logout}
                className="text-velvet_text-secondary hover:text-velvet_text-primary text-sm"
                data-testid="logout-button"
              >
                {t('logout')}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-32 pb-20 px-6">
        <div className="max-w-6xl mx-auto">
          <h1 className="font-heading text-4xl lg:text-5xl text-center gradient-text mb-4 animate-slideUp">
            {({fr: 'Choisissez votre partenaire', en: 'Choose your partner', es: 'Elige a tu pareja', de: 'Wähle deinen Partner', pt: 'Escolhe o teu parceiro'})[language] || 'Choose your partner'}
          </h1>
          <p className="text-velvet_text-secondary text-center mb-4">
            {genderLabels[gender]?.[language] || gender}
          </p>
          <p className="text-velvet_text-secondary text-center mb-16 max-w-xl mx-auto text-sm">
            {({fr: 'Sélectionnez la personnalité qui vous attire le plus', en: 'Select the personality that attracts you the most', es: 'Selecciona la personalidad que más te atraiga', de: 'Wähle die Persönlichkeit, die dich am meisten anzieht', pt: 'Seleciona a personalidade que mais te atrai'})[language] || 'Select the personality that attracts you the most'}
          </p>

          <div className="grid md:grid-cols-3 gap-8">
            {characters.map((character, index) => {
              const isUnlocked = character.is_unlocked || character.is_free;
              const isComingSoon = character.coming_soon === true;
              
              return (
                <div
                  key={character.id}
                  onClick={() => handleCardClick(character)}
                  className={`group relative overflow-hidden rounded-2xl glass ${isComingSoon ? 'opacity-90 cursor-default' : 'card-hover cursor-pointer'} animate-slideUp stagger-${index + 1}`}
                  data-testid={`character-card-${character.id}`}
                >
                  {/* Image */}
                  <div className="aspect-[3/4] overflow-hidden relative">
                    <SmartImage
                      src={character.image}
                      alt={character.name}
                      className="w-full h-full"
                      imgClassName={`transition-transform duration-700 ${isComingSoon ? 'filter brightness-[0.75] grayscale-[0.1]' : ''} ${!isComingSoon ? 'group-hover:scale-110' : ''} ${!isUnlocked && !isComingSoon ? 'filter brightness-[0.82]' : ''}`}
                      style={{ objectPosition: character.id === 'raphael' ? 'center 20%' : 'top' }}
                    />
                    
                    {/* Lock overlay for locked characters */}
                    {!isUnlocked && !isComingSoon && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="glass rounded-full p-4">
                          <Lock className="text-purple-light" size={32} />
                        </div>
                      </div>
                    )}
                    
                    {/* Badge */}
                    {isComingSoon ? (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="px-6 py-3 bg-terracotta-dark rounded-full text-white text-2xl font-extrabold tracking-wider shadow-2xl uppercase" style={{textShadow: '0 2px 8px rgba(0,0,0,0.4)'}}>
                          {language === 'fr' ? 'Bientôt' : language === 'es' ? 'Próximamente' : language === 'de' ? 'Demnächst' : language === 'pt' ? 'Em breve' : 'Soon'}
                        </span>
                      </div>
                    ) : (
                    <div className="absolute top-4 right-4">
                      {character.is_free ? (
                        // No badge for free characters — keep the card clean.
                        null
                      ) : isUnlocked ? (
                        // Already purchased / subscribed — show a discreet "unlocked" badge
                        // instead of the now-irrelevant price.
                        <span className="px-3 py-1 bg-green-600/90 rounded-full text-white text-xs font-semibold shadow-md backdrop-blur-sm">
                          {language === 'fr' ? 'Débloqué' : language === 'es' ? 'Desbloqueado' : language === 'de' ? 'Freigeschaltet' : language === 'pt' ? 'Desbloqueado' : 'Unlocked'}
                        </span>
                      ) : (
                        <span className="px-3 py-1 bg-black/80 border border-purple-main/50 rounded-full text-purple-light text-xs font-medium backdrop-blur-sm">
                          {playPrices.oneTime.full && (
                            <span className="line-through opacity-60 mr-1">{playPrices.oneTime.full}</span>
                          )}
                          {playPrices.oneTime.promo}
                        </span>
                      )}
                    </div>
                    )}
                  </div>
                  
                  {/* Content */}
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-heading text-2xl text-velvet_text-primary">
                        {character.name}
                      </h3>
                      {/* Voice preview button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          playVoicePreview(character);
                        }}
                        disabled={loadingVoice === character.id}
                        className={`p-2 rounded-full transition-all ${
                          playingVoice === character.id 
                            ? 'bg-purple-main text-white' 
                            : 'bg-purple-main/20 text-purple-light hover:bg-purple-main/40'
                        }`}
                        data-testid={`voice-preview-${character.id}`}
                        title={language === 'fr' ? 'Écouter la voix' : 'Listen to voice'}
                      >
                        {loadingVoice === character.id ? (
                          <Loader2 size={16} className="animate-spin" />
                        ) : playingVoice === character.id ? (
                          <VolumeX size={16} />
                        ) : (
                          <Volume2 size={16} />
                        )}
                      </button>
                    </div>
                    <p className="text-velvet_text-accent text-sm mb-3">
                      {character.personality}
                    </p>
                    <p className="text-velvet_text-secondary text-sm mb-4">
                      {character.description}
                    </p>
                    
                    {/* Action button */}
                    {isComingSoon ? (
                      <Button
                        disabled
                        className="w-full bg-terracotta-dark text-white font-bold rounded-xl cursor-not-allowed shadow-md hover:bg-terracotta-dark disabled:opacity-100"
                        data-testid={`coming-soon-${character.id}`}
                      >
                        {language === 'fr' ? 'Prochainement disponible' : language === 'es' ? 'Próximamente disponible' : language === 'de' ? 'Demnächst verfügbar' : language === 'pt' ? 'Disponível em breve' : 'Coming soon'}
                      </Button>
                    ) : isUnlocked ? (
                      <Button
                        onClick={() => handleSelect(character)}
                        className="w-full bg-purple-main hover:bg-purple-dark text-white rounded-xl"
                        data-testid={`select-${character.id}`}
                      >
                        {t('select')}
                      </Button>
                    ) : (
                      <Button
                        onClick={() => handlePurchase(character)}
                        disabled={purchasing === character.id}
                        className="w-full bg-gradient-to-r from-purple-main to-fuchsia-main hover:opacity-90 text-white rounded-xl"
                        data-testid={`buy-${character.id}`}
                      >
                        {purchasing === character.id ? (
                          <Loader2 className="animate-spin" size={18} />
                        ) : (
                          <>
                            <ShoppingCart size={16} className="mr-2" />
                            {({fr: 'Débloquer', es: 'Desbloquear', de: 'Freischalten', pt: 'Desbloquear'})[language] || 'Unlock'}{' '}
                            {playPrices.oneTime.full && (
                              <span className="line-through opacity-60 mr-1 ml-1">{playPrices.oneTime.full}</span>
                            )}
                            {playPrices.oneTime.promo}
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
            
            {/* Coming Soon Card */}
            <div
              className={`group relative overflow-hidden rounded-2xl glass animate-slideUp stagger-${characters.length + 1}`}
              data-testid="character-card-coming-soon"
            >
              {/* Placeholder Image */}
              <div className="aspect-[3/4] overflow-hidden relative bg-purple-main/5 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-24 h-24 mx-auto mb-4 rounded-full border-2 border-dashed border-purple-main/50 flex items-center justify-center">
                    <span className="text-4xl text-purple-light/70">?</span>
                  </div>
                </div>
                
                
                {/* Badge */}
                <div className="absolute top-4 right-4">
                  <span className="px-4 py-1.5 bg-terracotta-dark rounded-full text-white text-sm font-bold shadow-lg uppercase tracking-wide">
                    {({fr: 'Bientôt', en: 'Soon', es: 'Próximamente', de: 'Demnächst', pt: 'Em breve'})[language] || 'Soon'}
                  </span>
                </div>
              </div>
              
              {/* Content */}
              <div className="p-6">
                <h3 className="font-heading text-2xl text-velvet_text-primary/50 mb-1 italic">
                  {({fr: 'Bientôt...', en: 'Coming soon...', es: 'Próximamente...', de: 'Demnächst...', pt: 'Em breve...'})[language] || 'Coming soon...'}
                </h3>
                <p className="text-velvet_text-accent/50 text-sm mb-3 italic">
                  {({fr: 'Nouveau personnage', en: 'New character', es: 'Nuevo personaje', de: 'Neue Persönlichkeit', pt: 'Novo personagem'})[language] || 'New character'}
                </p>
                <p className="text-velvet_text-secondary/50 text-sm mb-4 italic">
                  {({
                    fr: 'De nouvelles personnalités seront bientôt disponibles pour enrichir votre expérience.',
                    en: 'New personalities will soon be available to enrich your experience.',
                    es: 'Pronto habrá nuevas personalidades disponibles para enriquecer tu experiencia.',
                    de: 'Bald werden neue Persönlichkeiten verfügbar sein, um dein Erlebnis zu bereichern.',
                    pt: 'Em breve estarão disponíveis novas personalidades para enriquecer a tua experiência.'
                  })[language] || 'New personalities will soon be available to enrich your experience.'}
                </p>
                
                {/* Disabled button */}
                <Button
                  disabled
                  className="w-full bg-terracotta-dark text-white font-bold rounded-xl cursor-not-allowed shadow-md hover:bg-terracotta-dark disabled:opacity-100"
                >
                  {({fr: 'Prochainement', en: 'Coming soon', es: 'Próximamente', de: 'Demnächst', pt: 'Em breve'})[language] || 'Coming soon'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Guest Purchase Warning Modal */}
      <AlertDialog open={guestWarningOpen} onOpenChange={setGuestWarningOpen}>
        <AlertDialogContent className="bg-velvet-bg border border-purple-main/30">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-velvet_text-primary flex items-center gap-2">
              <AlertTriangle className="text-amber-400" size={20} />
              {({fr: 'Attention - Mode Invité', en: 'Warning - Guest Mode', es: 'Atención - Modo Invitado', de: 'Achtung - Gastmodus', pt: 'Atenção - Modo Convidado'})[language] || 'Warning - Guest Mode'}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-velvet_text-secondary">
              {language === 'fr' 
                ? 'Vous êtes en mode invité. Vos achats ne seront PAS sauvegardés si vous quittez l\'application sans créer de compte. Créez un compte après votre achat pour conserver vos achats.'
                : 'You are in guest mode. Your purchases will NOT be saved if you leave the app without creating an account. Create an account after your purchase to keep your purchases.'
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel 
              className="border-purple-main/50 text-velvet_text-secondary hover:bg-purple-main/10"
              data-testid="guest-warning-cancel"
            >
              {({fr: 'Annuler', en: 'Cancel', es: 'Cancelar', de: 'Abbrechen', pt: 'Cancelar'})[language] || 'Cancel'}
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmGuestPurchase}
              className="bg-gradient-to-r from-purple-main to-fuchsia-main text-white hover:opacity-90"
              data-testid="guest-warning-confirm"
            >
              {({fr: 'Continuer l\'achat', en: 'Continue purchase', es: 'Continuar compra', de: 'Kauf fortsetzen', pt: 'Continuar compra'})[language] || 'Continue purchase'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <PurchaseCelebrationModal
        open={!!celebration}
        title={
          ({
            fr: 'Personnage débloqué !',
            en: 'Character unlocked!',
            es: '¡Personaje desbloqueado!',
            de: 'Charakter freigeschaltet!',
            pt: 'Personagem desbloqueada!',
          })[language] || 'Character unlocked!'
        }
        message={
          celebration?.characterName
            ? ({
                fr: `Tu peux maintenant discuter avec ${celebration.characterName}. Profite bien`,
                en: `You can now chat with ${celebration.characterName}. Enjoy`,
                es: `Ahora puedes chatear con ${celebration.characterName}. Disfruta`,
                de: `Du kannst jetzt mit ${celebration.characterName} chatten. Viel Spaß`,
                pt: `Já podes conversar com ${celebration.characterName}. Aproveita`,
              })[language] || `You can now chat with ${celebration.characterName}.`
            : ''
        }
        continueLabel={
          ({
            fr: 'Continuer',
            en: 'Continue',
            es: 'Continuar',
            de: 'Weiter',
            pt: 'Continuar',
          })[language] || 'Continue'
        }
        onContinue={() => {
          const path = celebration?.continueTo;
          setCelebration(null);
          if (path) navigate(path);
        }}
      />
    </div>
  );
}
